import React, {useContext, useEffect, useMemo, useState} from 'react';

import {
    Box,
    Typography,
    Grid,
    Paper,
    Avatar,
    Chip,
    Stack,
    Divider,
    IconButton,
    InputAdornment,
    TextField,
    Container,
    Tooltip, CircularProgress,
} from '@mui/material';
import {
    ReceiptLong as TransactionIcon,
    Search as SearchIcon,
    CreditCard as CardIcon,
    Apple as AppleIcon,
    Google as GoogleIcon,
    AccessTime as TimeIcon,
    FilterList as FilterIcon,
    ArrowForwardIos as ArrowIcon,
    AccountBalanceWallet as WalletIcon,
    Assessment as AnalyticsIcon,
    Payment as PaymentIcon
} from '@mui/icons-material';
import {PAContext} from "../../../Services/PAContext";
import {getAllEventsFromDatabase} from "../../../Services/BrowserDatabaseFunctions";

// The payment service mapping logic from your snippet
const getPaymentService = (service) => {
    switch (service) {
        case "Google": return "Google Pay";
        case "Stripe": return "Credit Card";
        case "Apple": return "Apple Pay";
        default: return "Credit Card";
    }
};

const getServiceIcon = (service) => {
    switch (service) {
        case "Google": return <GoogleIcon sx={{ fontSize: 18, color: '#4285F4' }} />;
        case "Apple": return <AppleIcon sx={{ fontSize: 18, color: '#000' }} />;
        default: return <PaymentIcon sx={{ fontSize: 18, color: '#6366f1' }} />;
    }
};



function Transactions(){

    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account } = dataArray;


    const [familyEvents, setFamilyEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Fetch data on mount
    useEffect(() => {
        let isMounted = true;
        setLoading(true);

        getAllEventsFromDatabase(Account.id).then((data) => {
            if (isMounted) {
                setFamilyEvents(data);
                setLoading(false);
            }
        }).catch(err => {
            console.error("Failed to fetch events:", err);
            if (isMounted) setLoading(false);
        });

        return () => { isMounted = false; };
    }, [Account.id]);


    // Filtering logic - uses the state variable familyEvents
    const filteredEvents = useMemo(() => {
        if (!familyEvents) return [];

        return familyEvents.map(event => ({
            ...event,
            // Use optional chaining and fallback to empty array if stripe_transactions is undefined
            stripe_transactions: (event?.stripe_transactions || []).filter(tx =>
                tx.item_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tx.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                event.name?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        })).filter(event => event.stripe_transactions.length > 0);
    }, [searchQuery, familyEvents]);


    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    };

    const formatTimestamp = (ts) => {
        const date = new Date(ts);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + " • " +
            date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    const totalSpent = useMemo(() => {
        if (!Array.isArray(familyEvents)) return 0;
        return familyEvents.reduce((sum, event) =>
            sum + (event?.stripe_transactions || []).reduce((s, tx) => s + (tx?.total || 0), 0), 0
        );
    }, [familyEvents]);


    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 6 }}>
            <Container maxWidth="lg">
                {/* Header Section */}
                <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 900, color: '#1a1a1a', mt: 0.5 }}>
                            Transactions
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>Total Lifecycle Spend</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#1a1a1a' }}>
                            {formatCurrency(totalSpent)}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={4}>
                    {/* Sidebar / Stats */}
                    <Grid item xs={12} md={3}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #eee' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AnalyticsIcon fontSize="small" color="primary" /> Overview
                                </Typography>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}>Active Events</Typography>
                                        <Typography variant="h6" fontWeight={800}>{familyEvents.length}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700}>Processing Method</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                            <Tooltip title="Apple Pay Ready"><AppleIcon fontSize="small" /></Tooltip>
                                            <Tooltip title="Google Pay Ready"><GoogleIcon fontSize="small" /></Tooltip>
                                            <Tooltip title="Stripe Secured"><CardIcon fontSize="small" /></Tooltip>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>

                    {/* Main Transaction List */}
                    <Grid item xs={12} md={9}>
                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            placeholder="Search by item, event, or description..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                mb: 4,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '16px',
                                    bgcolor: 'white',
                                    '& fieldset': { borderColor: '#eee' },
                                    '&:hover fieldset': { borderColor: '#f09637' },
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#ccc' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small"><FilterIcon /></IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Event Groups */}
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                                <CircularProgress sx={{ color: '#f09637' }} />
                            </Box>
                        ) : (
                            <Stack spacing={5}>
                                {filteredEvents.map((event) => (
                                    <Box key={event.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 1 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: '#1a1a1a',
                                                    width: 32,
                                                    height: 32,
                                                    mr: 2,
                                                    fontSize: '0.8rem',
                                                    fontWeight: 800
                                                }}
                                            >
                                                {event.name.charAt(0)}
                                            </Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a', flexGrow: 1 }}>
                                                {event.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                                {event.stripe_transactions.length} ITEMS
                                            </Typography>
                                        </Box>

                                        <Stack spacing={2}>
                                            {event.stripe_transactions.map((tx) => (
                                                <Paper
                                                    key={tx.id}
                                                    elevation={0}
                                                    sx={{
                                                        p: 3,
                                                        borderRadius: '24px',
                                                        border: '1px solid #f0f0f0',
                                                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        overflow: 'hidden',
                                                        '&:hover': {
                                                            borderColor: '#f09637',
                                                            boxShadow: '0 12px 24px rgba(240, 150, 55, 0.08)',
                                                            transform: 'translateY(-2px)'
                                                        }
                                                    }}
                                                >
                                                    <Grid container alignItems="center" spacing={3}>
                                                        {/* Transaction Detail */}
                                                        <Grid item xs={12} sm={5}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#1a1a1a', mb: 0.5 }}>
                                                                {tx.item_name}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', lineHeight: 1.5 }}>
                                                                {tx.description}
                                                            </Typography>
                                                        </Grid>

                                                        {/* Payment Method */}
                                                        <Grid item xs={12} sm={4}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Box sx={{
                                                                    width: 44,
                                                                    height: 44,
                                                                    borderRadius: '12px',
                                                                    bgcolor: '#f8f9fa',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    border: '1px solid #eee'
                                                                }}>
                                                                    {getServiceIcon(tx.from_service)}
                                                                </Box>
                                                                <Box>
                                                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#1a1a1a', display: 'block' }}>
                                                                        {getPaymentService(tx.from_service)}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                                        {tx.card_type} •••• {tx.last_four}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Grid>

                                                        {/* Pricing & Time */}
                                                        <Grid item xs={12} sm={3} sx={{ textAlign: { sm: 'right' } }}>
                                                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1a1a1a' }}>
                                                                {formatCurrency(tx.total)}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.5, color: 'text.disabled', mt: 0.5 }}>
                                                                <TimeIcon sx={{ fontSize: 14 }} />
                                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                    {formatTimestamp(tx.id)}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}

                                {filteredEvents.length === 0 && (
                                    <Box sx={{ textAlign: 'center', py: 12, bgcolor: 'white', borderRadius: '32px', border: '1px solid #eee' }}>
                                        <TransactionIcon sx={{ fontSize: 64, color: '#f0f0f0', mb: 2 }} />
                                        <Typography variant="h6" sx={{ color: '#ccc', fontWeight: 800 }}>No transactions found</Typography>
                                        <Typography variant="body2" color="text.secondary">Try adjusting your search filters</Typography>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )

}

export default Transactions;